import { Observable } from 'rxjs';
import { API_ENDPOINTS } from '@skinsoft/entity';
import { set } from 'lodash'
import { concatMap, first } from 'rxjs/operators';
import { ThesaurusTerm } from '../thesaurusTerm';

import { EntityDataStore } from '@skinsoft/entity-data-store';
import { TranslatedString } from '@skinsoft/i18n';
import { createTranslatedString, getCurrentDateAsSkinDate, SkinDate } from '@skinsoft/ui';
import _ from 'lodash';

export interface ConditionReport {
    id: string;
    associatedMedias: any;
    conditionReportCS: {
        alterationDetails: DefectElement[];
        referenceNumber: string;
        comments: any[];
        reportDate: SkinDate,
        signers: Signer[]
    };
}

export interface Signer {
    id: string,
    signer: TranslatedString
    signature: string
}

export interface DefectAnnotation {
    id: number,
    title: TranslatedString
    comments: TranslatedString[]
    media: string
    svg: string
}

export interface DefectAlteration {
    id: number,
    typeOfAlteration: ThesaurusTerm['id']
    precision: TranslatedString
    annotations: DefectAnnotation[]

}

export interface DefectMaterial {
    id: number,
    material: ThesaurusTerm['id']
    alterations: DefectAlteration[]
}

export interface DefectObservation {
    id: number,
    typeOfObservation: ThesaurusTerm['id']
    materialAlterations: DefectMaterial[];
}

export interface DefectElement {
    id: number;
    observationAlterationDetails: DefectObservation[]
    element: TranslatedString
    precision: TranslatedString
}

export enum ConditionReportState {
    IN_PROGRESS = "ui.lifecycle-state.inProgress",
    CLOSED = "ui.lifecycle-state.validated"
}

// Methods to create empty alterations tree parts
export const getMinimumDefectAlterations = (locale: string) => {
    return [
        {
            id: 0,
            // tslint:disable-next-line:object-literal-sort-keys
            element: createTranslatedString(locale),
            precision: createTranslatedString(locale),
            // tslint:disable-next-line:object-literal-sort-keys
            observationAlterationDetails: getMinimumObservations(locale)
        }
    ] as DefectElement[]
}

export const getMinimumObservations = (locale: string) => {
    return [
        {
            id: 0,
            typeOfObservation: "",
            // tslint:disable-next-line:object-literal-sort-keys
            materialAlterations: getMinimumMaterials(locale)
        }
    ] as DefectObservation[]
}

export const getMinimumMaterials = (locale: string) => {
    return [
        {
            id: 0,
            material: "",
            // tslint:disable-next-line:object-literal-sort-keys
            alterations: getMinimumAlterations(locale)
        }
    ] as DefectMaterial[]
}

export const getMinimumAlterations = (locale: string) => {
    return [
        {
            id: 0,
            typeOfAlteration: "",
            // tslint:disable-next-line:object-literal-sort-keys
            precision: createTranslatedString(locale),
            annotations: []
        }
    ] as DefectAlteration[]
}

export class ConditionReportDataStore extends EntityDataStore<ConditionReport> {
    public static getInstance() {
        if (!ConditionReportDataStore.instance) {
            ConditionReportDataStore.instance = new ConditionReportDataStore();
        }

        return ConditionReportDataStore.instance;
    }

    public static install(Vue) {
        Vue.prototype.$conditionReportDataStore = ConditionReportDataStore.getInstance();
    }

    private static instance: ConditionReportDataStore = null as any;

    constructor() {
        super('conditionReports', ['conditionReport']);
    }

    public exists(id: ConditionReport['id']) {
        return this.fetchOne(id)
            .pipe(first())
            .toPromise();
    }

    public closeConditionReport(
        reportId: string,
    ) {
        return this.proxy
            .fetch(`/conditionReports`, { id: reportId, lifecycleState: 'validated' }, 'PATCH')
            .pipe(
                concatMap(() => this.fetchOne(reportId, true)),
                first(),
            )
            .toPromise();
    }

    public duplicate(entityToDuplicateId: string) {
        return super.duplicate(entityToDuplicateId, undefined, {
            conditionReportCS: {
                reportDate: getCurrentDateAsSkinDate(),
            } as any
        })
    }

    public update(id: string, body: Partial<ConditionReport>, endpointOverride?: string): Observable<ConditionReport> {
        return super.update(id, body, endpointOverride || API_ENDPOINTS['conditionReport'])
    }

    public updateOfflineMode(id: string, body: Partial<ConditionReport>): Observable<ConditionReport> {
        return super.updateOfflineMode(id, body)
    }

    public async setInvolvedItemAndSave(reportId: string, itemId: string) {
        const report: ConditionReport = await this.fetchOne(reportId).pipe(first()).toPromise();

        set(report, 'conditionReportCS.linkableItem.linkableItem', itemId);
    }

}
